/**
 * @file checks.ts
 * @description Чистые проверки SEO для «Сайт → SEO → Проверка сайта» (по
 * живому содержимому) и окна публикации страницы (по черновику) —
 * phase19.16, партия Р3в, v0.19.64; site-builder.md SB132, SB127, SB49, SB92;
 * seo.md SEO12; db-design-r3v §6.1, §6.2.
 *
 * Здесь: главные заголовки H1 секций (логика `page-h1.ts` кабинета перенесена
 * сюда — apps/admin/src/lib/site-builder/page-h1.ts переходит на импорт),
 * фото без описания (правило витрины ImageElement.tsx: alt элемента, иначе
 * alt медиатеки), итоговый заголовок вкладки (правило витрины
 * apps/web/src/lib/site/site-seo.ts `pageTitle` + шаблон корневого layout —
 * повтор, а не импорт), длины заголовка и описания и проверки «Ключевых
 * запросов» (SEO12). Без запросов к базе: данные передаёт вызывающий.
 */

import type { SectionElement, SectionPayload } from "../site-section/schema.js";
import type { SiteSeoIssueCode, SiteSeoIssueLevel } from "./issues.js";

// ── Главный заголовок H1 ─────────────────────────────────────────────────

/** Что сейчас правится: шапка раздела или элемент с id (кабинет, uiux-design-r2 §10). */
export type H1Subject = { header: true } | { elementId: string };

/** Даёт ли элемент главный заголовок: заголовок H1 или «Название» товара (всегда H1, Р2а). */
function isH1Element(element: SectionElement): boolean {
  return (element.kind === "heading" && element.level === 1) || element.kind === "product-title";
}

/** Элементы секции; payload из базы может быть неполным — без колонок элементов нет. */
function sectionElements(payload: SectionPayload): SectionElement[] {
  return payload.columns?.items?.flatMap((column) => column.elements ?? []) ?? [];
}

/** Сколько главных заголовков в секции: шапка уровня 1 + элементы H1 и «Название» товара. */
export function sectionH1Count(payload: SectionPayload): number {
  const header = payload.header?.level === 1 ? 1 : 0;
  return header + sectionElements(payload).filter(isH1Element).length;
}

/** Сколько главных заголовков на странице (секции включённых блоков по порядку). */
export function countSectionH1(payloads: readonly SectionPayload[]): number {
  return payloads.reduce((sum, payload) => sum + sectionH1Count(payload), 0);
}

/** Есть ли в секции H1 кроме правимого места (`except` null — любой H1). */
export function sectionHasOtherH1(payload: SectionPayload, except: H1Subject | null): boolean {
  const exceptHeader = except !== null && "header" in except;
  const exceptId = except !== null && "elementId" in except ? except.elementId : null;
  if (payload.header?.level === 1 && !exceptHeader) return true;
  return sectionElements(payload).some((el) => isH1Element(el) && el.id !== exceptId);
}

/** Есть ли в секции элемент «Название» товара (шаблон страницы товара, Р2а). */
export function sectionHasProductTitle(payload: SectionPayload): boolean {
  return sectionElements(payload).some((el) => el.kind === "product-title");
}

// ── Фото без описания ────────────────────────────────────────────────────

/** Описание файла в медиатеке по id: Map или функция; нет — описания нет. */
export type MediaAltLookup =
  | ReadonlyMap<string, string | null | undefined>
  | ((mediaId: string) => string | null | undefined);

/** Фото секции без описания: элемент и файл. */
export interface SectionImageWithoutAlt {
  readonly elementId: string;
  readonly kind: "image" | "gallery";
  readonly mediaId: string;
}

const hasText = (value: string | null | undefined): boolean => typeof value === "string" && value.trim().length > 0;

/**
 * Фото секции без описания — по правилу витрины: у «Фото» alt элемента,
 * иначе alt файла медиатеки (ImageElement.tsx); у галереи — alt файла
 * (MediaElements.tsx). Не проверяются: место под фото без файла (на сайте
 * не показывается, SB21), фон и декор (`alt=""` намеренно), плитки (подпись
 * плитки — её текст), фото товаров (витрина подставляет название, SB132).
 */
export function sectionImagesWithoutAlt(payload: SectionPayload, mediaAlt?: MediaAltLookup): SectionImageWithoutAlt[] {
  const altOf = (mediaId: string): string | null | undefined => {
    if (!mediaAlt) return undefined;
    return typeof mediaAlt === "function" ? mediaAlt(mediaId) : mediaAlt.get(mediaId);
  };
  const found: SectionImageWithoutAlt[] = [];
  for (const element of sectionElements(payload)) {
    if (element.kind === "image") {
      if (element.mediaId && !hasText(element.alt) && !hasText(altOf(element.mediaId))) {
        found.push({ elementId: element.id, kind: "image", mediaId: element.mediaId });
      }
    } else if (element.kind === "gallery") {
      for (const mediaId of element.mediaIds) {
        if (!hasText(altOf(mediaId))) found.push({ elementId: element.id, kind: "gallery", mediaId });
      }
    }
  }
  return found;
}

// ── Итоговый заголовок вкладки ───────────────────────────────────────────

export interface EffectiveSeoTitleInput {
  /** Название страницы, товара, статьи (у категории — «<название> — Каталог», как на витрине). */
  readonly pageTitle: string;
  /** Своё SEO-поле (`seoTitle` / `metaTitle`): есть текст — берётся целиком. */
  readonly seoTitle?: string | null;
  /**
   * Окончание заголовков магазина (`seo.titleSuffix`, SB88: « — Название»)
   * или шаблон с `%s`. Действует, только если у сайта есть заголовок.
   */
  readonly template?: string | null;
  /** Заголовок сайта по умолчанию: `seo.defaultTitle`, иначе название сайта. */
  readonly siteTitle?: string | null;
  /** Главная: без окончания — её заголовок и есть заголовок сайта. */
  readonly home?: boolean;
}

/**
 * Итоговый `<title>` так, как его соберёт витрина:
 *   - своё SEO-поле с текстом — целиком (`{ absolute }` в `pageTitle`);
 *   - главная — заголовок сайта (app/page.tsx), без окончания;
 *   - иначе название + окончание; у сайта без заголовка шаблон корневого
 *     layout — «%s», окончание не добавляется (`buildRootMetadata`).
 */
export function effectiveSeoTitle(input: EffectiveSeoTitleInput): string {
  const custom = input.seoTitle?.trim();
  if (custom) return custom;
  const siteTitle = input.siteTitle?.trim() ?? "";
  if (input.home) return siteTitle;
  const name = input.pageTitle.trim();
  const template = input.template ?? "";
  if (!siteTitle || !template) return name;
  return template.includes("%s") ? template.replace("%s", name) : `${name}${template}`;
}

// ── Длины ────────────────────────────────────────────────────────────────

/** Поиск обрезает заголовок длиннее 60 знаков (SB132). */
export const SEO_TITLE_MAX = 60;
/** Поиск обрезает описание длиннее 160 знаков (SB132). */
export const SEO_DESCRIPTION_MAX = 160;

/** Замечание о длине: сколько знаков и предел. */
export interface SeoLengthIssue {
  readonly code: Extract<SiteSeoIssueCode, "seo_title_long" | "seo_description_long">;
  readonly level: Extract<SiteSeoIssueLevel, "warning">;
  readonly length: number;
  readonly limit: number;
}

/** Длина в знаках (символы Unicode, как `length()` в PostgreSQL), без пробелов по краям. */
export function seoTextLength(text: string | null | undefined): number {
  return text ? [...text.trim()].length : 0;
}

/** Заголовок длиннее 60 знаков — предупреждение. */
export function checkTitleLength(title: string | null | undefined): SeoLengthIssue | null {
  const length = seoTextLength(title);
  return length > SEO_TITLE_MAX ? { code: "seo_title_long", level: "warning", length, limit: SEO_TITLE_MAX } : null;
}

/** Описание длиннее 160 знаков — предупреждение. */
export function checkDescriptionLength(description: string | null | undefined): SeoLengthIssue | null {
  const length = seoTextLength(description);
  return length > SEO_DESCRIPTION_MAX
    ? { code: "seo_description_long", level: "warning", length, limit: SEO_DESCRIPTION_MAX }
    : null;
}

/** Нет своего описания (подставится описание сайта) — сведение, у страниц и статей (SB132). */
export function isSeoDescriptionEmpty(description: string | null | undefined): boolean {
  return seoTextLength(description) === 0;
}

// ── Ключевые запросы (SEO11–SEO12) ───────────────────────────────────────

/**
 * Запрос для сравнения: нижний регистр, «ё» как «е», пробелы схлопнуты.
 * Словоформы не разбираем (решение плана Р3в): «иван-чай» найдётся в
 * «Иван-чай ферментированный», а «иван-чая» — нет.
 */
export function normalizeSeoKeyword(text: string | null | undefined): string {
  return (text ?? "").toLowerCase().replace(/ё/gu, "е").replace(/\s+/gu, " ").trim();
}

export interface KeywordCheckInput {
  /** Главный запрос из «Ключевых запросов»; пусто — проверки нет. */
  readonly primary: string | null | undefined;
  /** Итоговый заголовок для поиска (`effectiveSeoTitle`). */
  readonly title: string | null | undefined;
  /** Текст главного заголовка; несколько H1 — все. */
  readonly h1?: string | readonly string[] | null;
}

/** Сведение SEO12: главного запроса нет ни в заголовке для поиска, ни в H1. */
export interface KeywordMissingIssue {
  readonly code: Extract<SiteSeoIssueCode, "seo_keyword_missing">;
  readonly level: Extract<SiteSeoIssueLevel, "info">;
  readonly primary: string;
}

/** SEO12: главный запрос есть в заголовке для поиска или в H1 (вхождение без регистра). */
export function checkKeywordInTitleOrH1(input: KeywordCheckInput): KeywordMissingIssue | null {
  const primary = normalizeSeoKeyword(input.primary);
  if (!primary) return null;
  const h1 = typeof input.h1 === "string" ? [input.h1] : (input.h1 ?? []);
  const places = [input.title, ...h1].map(normalizeSeoKeyword);
  if (places.some((place) => place.includes(primary))) return null;
  return { code: "seo_keyword_missing", level: "info", primary: input.primary?.trim() ?? primary };
}

/** Группа адресов с одним главным запросом. */
export interface DuplicateKeywordGroup<T> {
  /** Запрос в виде для сравнения (`normalizeSeoKeyword`). */
  readonly keyword: string;
  readonly items: T[];
}

/**
 * SEO12: «этот главный запрос уже у другой страницы» — группы от двух
 * адресов с одинаковым главным запросом (после `normalizeSeoKeyword`),
 * в порядке первого появления. Пустые запросы не участвуют.
 */
export function findDuplicatePrimaryKeywords<T extends { readonly primary: string | null | undefined }>(
  items: readonly T[],
): DuplicateKeywordGroup<T>[] {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const keyword = normalizeSeoKeyword(item.primary);
    if (!keyword) continue;
    const list = groups.get(keyword);
    if (list) list.push(item);
    else groups.set(keyword, [item]);
  }
  return [...groups.entries()].filter(([, list]) => list.length > 1).map(([keyword, list]) => ({ keyword, items: list }));
}
